import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPlus, FiSearch, FiX, FiArrowDownCircle, FiArrowUpCircle,
  FiFilter, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiPackage, FiArchive, FiTruck,
  FiFileText, FiDownload
} from 'react-icons/fi';
import {
  getMovements, createMovement, anularMovement,
  getWarehouses, getWarehouseStock, getProviders,
  getMovementCauseTypes, getEmpresaInfo
} from '../../../services/api';
import { exportPDF, exportExcel } from '../../../utils/exportMovimientos';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/ModuleTable.css';
import './MovimientosMaintenance.css';

/* ── Badge tipo de movimiento ─────────────────────────────────────────── */
const TYPE_META = {
  ingreso:      { label: 'INGRESO',    cls: 'badge-in' },
  salida:       { label: 'SALIDA',     cls: 'badge-out' },
  conversion:   { label: 'CONVERSIÓN', cls: 'badge-conv' },
  sale:         { label: 'VENTA',      cls: 'badge-sale' },
  purchase:     { label: 'COMPRA',     cls: 'badge-purchase' },
  adjustment:   { label: 'AJUSTE',     cls: 'badge-adj' },
  return:       { label: 'DEVOL.',     cls: 'badge-adj' },
  transferencia:{ label: 'TRANSFER.',  cls: 'badge-adj' },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { label: type?.toUpperCase(), cls: 'badge-adj' };
  return <span className={`mov-badge ${meta.cls}`}>{meta.label}</span>;
}

/* ── Formato fecha ───────────────────────────────────────────────────── */
function fmtDt(str) {
  if (!str) return '-';
  const d = new Date(str);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtQty(n) {
  return parseFloat(n ?? 0).toFixed(3).replace(/\.?0+$/, '');
}

/* ── MODAL NUEVO MOVIMIENTO ───────────────────────────────────────────── */
function ModalMovimiento({ businessId, warehouses, onClose, onSaved }) {
  const [tipo, setTipo]             = useState('ingreso');
  const [warehouseId, setWh]        = useState(warehouses[0]?.id || '');
  const [productSearch, setPSearch] = useState('');
  const [products, setProducts]     = useState([]);
  const [product, setProduct]       = useState(null);
  const [providers, setProviders]   = useState([]);
  const [providerId, setProvId]     = useState('');
  const [provSearch, setProvSearch] = useState('');
  const [causaTypes, setCausaTypes] = useState([]);
  const [causa, setCausa]           = useState('');
  const [cantidad, setCantidad]     = useState('');
  const [cantUnidad, setCantUnidad] = useState('');  // solo para special_condition en INGRESO
  const [notas, setNotas]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const searchRef = useRef(null);

  // Cargar proveedores una vez
  useEffect(() => {
    getProviders(businessId).then(r => setProviders(r.data?.data || []));
  }, [businessId]);

  // Cargar productos del almacén seleccionado
  const loadProducts = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const { data } = await getWarehouseStock(warehouseId, businessId, { search: productSearch });
      setProducts(data?.data || []);
    } catch { setProducts([]); }
  }, [warehouseId, businessId, productSearch]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Al cambiar almacén limpiar producto y cantidades
  useEffect(() => { setProduct(null); setPSearch(''); setCantidad(''); setCantUnidad(''); }, [warehouseId]);
  useEffect(() => {
    setCausa(''); setProvId(''); setProvSearch('');
    getMovementCauseTypes(businessId, tipo, true).then(r => {
      let causas = r.data?.data || [];
      // COMPRA se reserva para el módulo Compras; no disponible en movimientos manuales
      if (tipo === 'ingreso') causas = causas.filter(c => c.name !== 'COMPRA');
      setCausaTypes(causas);
    });
  }, [tipo, businessId]);

  const esEspecial  = !!product?.special_condition;
  const stockDisp   = product ? fmtQty(product.stock) : '-';
  const unidadDisp  = product ? parseInt(product.stock_unidad || 0) : '-';

  // Proveedores filtrados por búsqueda
  const filteredProviders = providers.filter(p => {
    const q = provSearch.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.ruc?.includes(q);
  });

  const filteredProducts = products.filter(p => {
    const q = productSearch.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
  });

  async function handleSave() {
    if (!warehouseId || !product || !causa) {
      setError('Completa almacén, producto y causa.'); return;
    }
    if (tipo === 'ingreso' && !providerId) {
      setError('Selecciona el proveedor para un ingreso.'); return;
    }

    const qty    = Number(cantidad    || 0);
    const unidad = Number(cantUnidad  || 0);

    // Validación de cantidades
    if (esEspecial) {
      if (qty <= 0 && unidad <= 0) {
        setError('Ingresa al menos una cantidad: stock o unidades (no ambas en 0).'); return;
      }
    } else {
      if (qty <= 0) {
        setError('La cantidad debe ser mayor a 0.'); return;
      }
    }

    // Validación de stock suficiente (solo SALIDA) — se omite si el almacén permite stock negativo
    const selectedWarehouse = warehouses.find(w => w.id === Number(warehouseId));
    if (tipo === 'salida' && !selectedWarehouse?.allow_negative_stock) {
      if (qty > 0 && qty > parseFloat(product.stock)) {
        setError(`Stock insuficiente: disponible ${stockDisp} ${product.unit_measure}`); return;
      }
      if (esEspecial && unidad > 0 && unidad > parseInt(product.stock_unidad || 0)) {
        setError(`Unidades insuficientes: disponible ${unidadDisp} piezas`); return;
      }
    }

    setSaving(true); setError('');
    try {
      await createMovement(businessId, {
        warehouse_id:     warehouseId,
        product_id:       product.id,
        provider_id:      tipo === 'ingreso' ? providerId || undefined : undefined,
        causa,
        movement_type:    tipo,
        qty_moved:        qty,
        qty_unidad_moved: esEspecial ? unidad : 0,  // aplica en ingreso Y salida
        notes:            notas || undefined
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  }

  return (
    <div className="mt-modal-overlay">
      <div className="mt-modal mov-modal" onClick={e => e.stopPropagation()}>
        <div className="mt-modal-header">
          <span>Nuevo Movimiento de Inventario</span>
          <button className="btn-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mt-modal-body">

          {/* Tipo */}
          <div className="mov-tipo-toggle">
            <button
              className={`mov-tipo-btn ${tipo === 'ingreso' ? 'active-in' : ''}`}
              onClick={() => setTipo('ingreso')}
            >
              <FiArrowDownCircle /> INGRESO
            </button>
            <button
              className={`mov-tipo-btn ${tipo === 'salida' ? 'active-out' : ''}`}
              onClick={() => setTipo('salida')}
            >
              <FiArrowUpCircle /> SALIDA
            </button>
          </div>

          <div className="mt-form-grid" style={{ marginTop: 18 }}>

            {/* Almacén */}
            <div className="mt-field">
              <label>Almacén <span className="req">*</span></label>
              <select value={warehouseId} onChange={e => setWh(e.target.value)}>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>

            {/* Causa */}
            <div className="mt-field">
              <label>Causa <span className="req">*</span></label>
              <select value={causa} onChange={e => setCausa(e.target.value)}>
                <option value="">— Seleccionar —</option>
                {causaTypes.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Proveedor — solo INGRESO */}
            {tipo === 'ingreso' && (
              <div className="mt-field mt-full">
                <label><FiTruck size={12} /> Proveedor <span className="req">*</span></label>
                <div className="mov-product-search">
                  <FiSearch size={13} />
                  <input
                    type="text"
                    placeholder="Buscar proveedor por nombre o RUC..."
                    value={provSearch}
                    onChange={e => { setProvSearch(e.target.value); setProvId(''); }}
                  />
                </div>
                <div className="mov-product-list" style={{ maxHeight: 68 }}>
                  {filteredProviders.length === 0 && (
                    <div className="mov-product-empty">Sin resultados</div>
                  )}
                  {filteredProviders.map(p => (
                    <div
                      key={p.id}
                      className={`mov-product-item ${providerId == p.id ? 'selected' : ''}`}
                      onClick={() => { setProvId(p.id); setProvSearch(p.name); }}
                    >
                      <span className="pitem-sku">{p.ruc || '—'}</span>
                      <span className="pitem-name">{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Producto — primero para que el usuario lo seleccione antes de ver cantidades */}
            <div className={`mt-field ${tipo === 'ingreso' ? '' : 'mt-full'}`}>
              <label>Producto <span className="req">*</span></label>
              <div className="mov-product-search">
                <FiSearch size={13} />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Buscar por nombre o SKU..."
                  value={productSearch}
                  onChange={e => { setPSearch(e.target.value); setProduct(null); }}
                />
              </div>
              <div className="mov-product-list">
                {filteredProducts.length === 0 && <div className="mov-product-empty">Sin resultados</div>}
                {filteredProducts.map(p => (
                  <div
                    key={p.id}
                    className={`mov-product-item ${product?.id === p.id ? 'selected' : ''}`}
                    onClick={() => { setProduct(p); setPSearch(p.name); setCantidad(''); setCantUnidad(''); }}
                  >
                    <span className="pitem-sku">{p.sku}</span>
                    <span className="pitem-name">{p.name}</span>
                    <span className={`pitem-stock ${parseFloat(p.stock) <= 0 ? 'stock-zero' : ''}`}>
                      {fmtQty(p.stock)} {p.unit_measure}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Cantidades — aparecen solo después de seleccionar producto ── */}
            {product && (
              <>
                {/* INGRESO con special_condition: dos campos */}
                {esEspecial ? (
                  <div className="mt-field mt-full">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 22px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <label style={{ fontSize: 11.5, fontWeight: 600, color: '#475569' }}>
                          Cantidad ({product.unit_measure}) <span className="req">*</span>
                          {tipo === 'salida' && <span className="field-hint">(disp: {stockDisp})</span>}
                        </label>
                        <input
                          className="mov-qty-input"
                          type="number" min="0" step="0.001"
                          value={cantidad}
                          onChange={e => setCantidad(e.target.value)}
                          placeholder="0.000"
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <label style={{ fontSize: 11.5, fontWeight: 600, color: '#475569' }}>
                          Unidades (piezas) <span className="req">*</span>
                          {tipo === 'salida' && <span className="field-hint">(disp: {unidadDisp})</span>}
                        </label>
                        <input
                          className="mov-qty-input"
                          type="number" min="0" step="1"
                          value={cantUnidad}
                          onChange={e => setCantUnidad(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#d97706', marginTop: 5 }}>
                      Al menos uno debe ser mayor a 0. Puedes dejar el otro en 0.
                    </div>
                  </div>
                ) : (
                  /* Un solo campo — con stock disponible solo en SALIDA */
                  <div className="mt-field">
                    <label>
                      Cantidad ({product.unit_measure}) <span className="req">*</span>
                      {tipo === 'salida' && <span className="field-hint">(disp: {stockDisp})</span>}
                    </label>
                    <input
                      type="number" min="0.001" step="0.001"
                      value={cantidad}
                      onChange={e => setCantidad(e.target.value)}
                      placeholder="0.000"
                    />
                  </div>
                )}
              </>
            )}

            {/* Notas */}
            <div className="mt-field mt-full">
              <label>Notas / Observaciones</label>
              <textarea
                rows={2} value={notas}
                onChange={e => setNotas(e.target.value)}
                placeholder="Opcional..."
              />
            </div>
          </div>

          {error && <div className="mov-error">{error}</div>}
        </div>

        <div className="mt-modal-footer">
          <button className="btn-modificar" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
          <button className="btn-salir" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ── VISTA KARDEX ─────────────────────────────────────────────────────── */
function KardexView({ rows, productName, warehouseName }) {
  if (!rows.length) {
    return <div className="mov-kardex-empty">Sin movimientos registrados para este producto con los filtros seleccionados.</div>;
  }

  const first         = rows[0];
  const last          = rows[rows.length - 1];
  const initialStock  = parseFloat(first.qty_before);
  const finalStock    = parseFloat(last.qty_after);
  const unit          = first.unit_measure || '';
  const isSpec        = !!first.special_condition;
  const totalIn       = rows.reduce((s, r) => s + parseFloat(r.entrada || 0), 0);
  const totalOut      = rows.reduce((s, r) => s + parseFloat(r.salida  || 0), 0);

  return (
    <div className="mov-kardex">
      {/* ── Encabezado ── */}
      <div className="mov-kardex-header">
        <FiPackage size={15} />
        <div className="kardex-hdr-info">
          <span className="kardex-title">KARDEX — {productName}</span>
          {warehouseName && <span className="kardex-warehouse">{warehouseName}</span>}
        </div>
        <div className="kardex-hdr-stats">
          <span className="kdx-stat green"><FiArrowDownCircle size={12}/> +{fmtQty(totalIn)} {unit}</span>
          <span className="kdx-stat red"><FiArrowUpCircle size={12}/> -{fmtQty(totalOut)} {unit}</span>
          <span className="kdx-stat neutral">{rows.length} movs.</span>
        </div>
      </div>

      {/* ── Fila de saldo inicial ── */}
      <div className="kardex-initial-row">
        <span className="kardex-initial-label">◈ SALDO INICIAL DE APERTURA</span>
        <div className="kardex-initial-values">
          <span className="kardex-initial-stock">{fmtQty(initialStock)} <em>{unit}</em></span>
          {isSpec && parseInt(first.qty_before) !== initialStock && (
            <span className="kardex-initial-unidades">{parseInt(first.qty_before)} piezas</span>
          )}
        </div>
      </div>

      {/* ── Tabla de movimientos ── */}
      <div className="mt-table-wrap">
        <table className="mt-table kardex-tbl">
          <thead>
            <tr className="mt-head-row">
              <th style={{ width: 28 }}>#</th>
              <th>Fecha / Hora</th>
              <th>Almacén</th>
              <th>Tipo</th>
              <th>Causa</th>
              <th>Proveedor</th>
              <th style={{ textAlign: 'right' }}>Saldo Ant.</th>
              <th style={{ textAlign: 'right', color: '#16a34a' }}>Entrada</th>
              <th style={{ textAlign: 'right', color: '#dc2626' }}>Salida</th>
              <th style={{ textAlign: 'right' }}>Saldo Act.</th>
              {isSpec && <th style={{ textAlign: 'right' }}>Uds. Mov.</th>}
              <th>Usuario</th>
              <th>Notas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isIn  = ['ingreso','purchase'].includes(r.movement_type) || ['TRANSFERENCIA ENTRADA','CONVERSION ENTRADA'].includes(r.causa);
              const isOut = ['salida','sale'].includes(r.movement_type)      || ['TRANSFERENCIA SALIDA', 'CONVERSION SALIDA'].includes(r.causa);
              const rowCls = isIn ? 'kdx-row-in' : isOut ? 'kdx-row-out' : '';
              return (
                <tr key={r.id} className={`mt-row-even ${rowCls}`}>
                  <td className="kdx-num">{i + 1}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDt(r.moved_at)}</td>
                  <td>
                    <span className="mov-warehouse-chip" style={{ fontSize: 11 }}>
                      <FiArchive size={10} />{r.warehouse_name || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`kdx-type-badge ${isIn ? 'kdx-in' : isOut ? 'kdx-out' : 'kdx-other'}`}>
                      {isIn ? '▲' : isOut ? '▼' : '◆'} {TYPE_META[r.movement_type]?.label || r.movement_type}
                    </span>
                  </td>
                  <td style={{ fontSize: 11.5 }}>{r.causa || '-'}</td>
                  <td style={{ fontSize: 11.5, color: '#475569' }}>{r.provider_name || '-'}</td>
                  <td className="kdx-saldo-ant">{fmtQty(r.qty_before)} <em style={{ fontStyle: 'normal', fontSize: 10.5 }}>{unit}</em></td>
                  <td className="kdx-entrada">
                    {parseFloat(r.entrada) > 0 ? <>+{fmtQty(r.entrada)} <em style={{ fontStyle: 'normal', fontSize: 10.5 }}>{unit}</em></> : <span className="kdx-dash">—</span>}
                  </td>
                  <td className="kdx-salida">
                    {parseFloat(r.salida) > 0 ? <>-{fmtQty(r.salida)} <em style={{ fontStyle: 'normal', fontSize: 10.5 }}>{unit}</em></> : <span className="kdx-dash">—</span>}
                  </td>
                  <td className="kdx-saldo-act">{fmtQty(r.qty_after)} <em style={{ fontStyle: 'normal', fontSize: 10.5 }}>{unit}</em></td>
                  {isSpec && (
                    <td style={{ textAlign: 'right', fontSize: 11.5, color: '#7c3aed' }}>
                      {parseInt(r.qty_unidad_moved || 0) > 0
                        ? `${isIn ? '+' : '-'}${parseInt(r.qty_unidad_moved)} UND`
                        : <span className="kdx-dash">—</span>}
                    </td>
                  )}
                  <td style={{ fontSize: 11.5 }}>{r.username || '-'}</td>
                  <td style={{ fontSize: 11, color: '#64748b', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.notes || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="kdx-footer-row">
              <td colSpan={isSpec ? 9 : 8}></td>
              <td className="kdx-saldo-final">
                <div className="kdx-final-label">Saldo Final</div>
                <div className="kdx-final-value">{fmtQty(finalStock)} <em>{unit}</em></div>
              </td>
              {isSpec && <td></td>}
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── COMPONENTE PRINCIPAL ─────────────────────────────────────────────── */
export default function MovimientosMaintenance({ businessId }) {
  const { user } = useAuth();
  const todayDate = new Date();
  const today = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
  const firstOfMonth = today.slice(0, 8) + '01';

  const [fromDate, setFromDate]       = useState(firstOfMonth);
  const [toDate, setToDate]           = useState(today);
  const [whFilter, setWhFilter]       = useState('');
  const [typeFilter, setTypeFilter]   = useState('');
  const [productFilter, setPFilter]   = useState('');
  const [productId, setProductId]     = useState('');

  const [movements, setMovements]     = useState([]);
  const [total, setTotal]             = useState(0);
  const [kardexRows, setKardex]       = useState([]);
  const [warehouses, setWarehouses]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [kardexLoading, setKL]        = useState(false);
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(50);
  const [showModal, setShowModal]     = useState(false);
  const [exporting, setExporting]     = useState(false);
  const [hideAnulados, setHideAnulados] = useState(true);
  const [kardexProductName, setKPN]   = useState('');
  const [kardexWhName, setKWN]        = useState('');
  const [kardexWhId, setKardexWhId]   = useState('');

  useEffect(() => {
    if (!businessId) return;
    getWarehouses(businessId).then(r => setWarehouses(r.data?.data || []));
  }, [businessId]);

  const loadMovements = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const params = { business_id: businessId, from_date: fromDate, to_date: toDate, page, limit };
      if (whFilter)                       params.warehouse_id     = whFilter;
      if (typeFilter)                     params.movement_type    = typeFilter;
      if (hideAnulados)                   params.exclude_anulados = 'true';
      if (productId)                      params.product_id       = productId;
      else if (productFilter.trim())      params.search           = productFilter.trim();
      const { data } = await getMovements(params);
      setMovements(data?.data || []);
      setTotal(data?.total || 0);
    } catch { setMovements([]); } finally { setLoading(false); }
  }, [businessId, fromDate, toDate, whFilter, typeFilter, productId, productFilter, hideAnulados, page, limit]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  const loadKardex = useCallback(async () => {
    if (!businessId || !productId) { setKardex([]); return; }
    setKL(true);
    try {
      const params = { business_id: businessId, product_id: productId, from_date: fromDate, to_date: toDate, kardex: true };
      // Prioridad: almacén de la fila seleccionada → filtro global de almacén
      if (kardexWhId) params.warehouse_id = kardexWhId;
      else if (whFilter) params.warehouse_id = whFilter;
      const { data } = await getMovements(params);
      setKardex(data?.data || []);
    } catch { setKardex([]); } finally { setKL(false); }
  }, [businessId, productId, kardexWhId, whFilter, fromDate, toDate]);

  useEffect(() => { loadKardex(); }, [loadKardex]);

  function handleSelectProduct(mov) {
    if (String(productId) === String(mov.product_id) && String(kardexWhId) === String(mov.warehouse_id)) {
      setProductId(''); setPFilter(''); setKPN(''); setKWN(''); setKardexWhId('');
    } else {
      setProductId(mov.product_id);
      setPFilter(mov.product_name);
      setKPN(`${mov.sku} — ${mov.product_name}`);
      // Usar el almacén de la fila clicada para el Kardex
      setKWN(mov.warehouse_name || '');
      setKardexWhId(mov.warehouse_id || '');
    }
  }

  function clearProduct() { setProductId(''); setPFilter(''); setKPN(''); setKWN(''); setKardexWhId(''); }

  async function handleExport(format) {
    if (exporting) return;
    setExporting(true);
    try {
      // Cargar TODOS los registros con los filtros actuales (sin paginación)
      const params = { business_id: businessId, from_date: fromDate, to_date: toDate, page: 1, limit: 10000 };
      if (whFilter)              params.warehouse_id  = whFilter;
      if (typeFilter)            params.movement_type = typeFilter;
      if (productId)             params.product_id    = productId;
      else if (productFilter.trim()) params.search    = productFilter.trim();

      const [movRes, empRes] = await Promise.all([
        getMovements(params),
        getEmpresaInfo(businessId),
      ]);

      const allData = movRes.data?.data || [];
      const empresa = empRes.data?.data || {};

      const warehouseName = whFilter
        ? warehouses.find(w => String(w.id) === String(whFilter))?.name || ''
        : '';

      const exportArgs = {
        empresa,
        data: allData,
        filters: {
          fromDate, toDate,
          warehouse: warehouseName,
          type:      typeFilter,
          product:   productFilter || '',
        },
        totals: {
          count:    movRes.data?.total || allData.length,
          totalIn:  allData.reduce((s, r) => (['ingreso','purchase'].includes(r.movement_type) || ['TRANSFERENCIA ENTRADA','CONVERSION ENTRADA'].includes(r.causa)) ? s + parseFloat(r.qty_moved) : s, 0),
          totalOut: allData.reduce((s, r) => (['salida','sale'].includes(r.movement_type) || ['TRANSFERENCIA SALIDA','CONVERSION SALIDA'].includes(r.causa)) ? s + parseFloat(r.qty_moved) : s, 0),
        },
        usuario: user?.full_name || user?.username || 'sistema',
      };

      if (format === 'pdf')   exportPDF(exportArgs);
      if (format === 'excel') exportExcel(exportArgs);
    } catch (e) {
      console.error('Error al exportar:', e);
    } finally {
      setExporting(false);
    }
  }

  async function handleAnular(mov) {
    if (!window.confirm(`¿Anular el movimiento #${mov.id}?\nSe revertirá el stock. El registro quedará marcado como ANULADO.`)) return;
    try {
      await anularMovement(mov.id, businessId);
      loadMovements();
      if (productId) loadKardex();
    } catch (e) {
      alert('Error al anular: ' + (e.response?.data?.error || e.message));
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const totalIn  = movements.reduce((s, r) => (!r.anulado && (['ingreso','purchase'].includes(r.movement_type) || ['TRANSFERENCIA ENTRADA','CONVERSION ENTRADA'].includes(r.causa))) ? s + parseFloat(r.qty_moved) : s, 0);
  const totalOut = movements.reduce((s, r) => (!r.anulado && (['salida','sale'].includes(r.movement_type) || ['TRANSFERENCIA SALIDA','CONVERSION SALIDA'].includes(r.causa))) ? s + parseFloat(r.qty_moved) : s, 0);

  return (
    <div className="mt-container">
      <div className="mt-section-title">
        <h3>MOVIMIENTOS DE INVENTARIO</h3>
        <p>Ingresos y salidas de stock por almacén · Clic en fila para ver Kardex del producto</p>
      </div>

      {/* ── FILTROS ── */}
      <div className="mov-filters">
        <div className="mov-filter-group">
          <label>Desde</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div className="mov-filter-group">
          <label>Hasta</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <div className="mov-filter-group">
          <label>Almacén</label>
          <select value={whFilter} onChange={e => { setWhFilter(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="mov-filter-group">
          <label>Tipo</label>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
            <option value="">Todos</option>
            <option value="ingreso">INGRESO</option>
            <option value="salida">SALIDA</option>
            <option value="conversion">CONVERSIÓN</option>
            <option value="adjustment">AJUSTE</option>
          </select>
        </div>
        <div className="mov-filter-group mov-filter-search">
          <label>Producto</label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Nombre o SKU..."
              value={productFilter}
              onChange={e => { setPFilter(e.target.value); if (!e.target.value) clearProduct(); }}
              onKeyDown={e => e.key === 'Enter' && loadMovements()}
            />
            {productId && (
              <button className="mov-clear-btn" onClick={clearProduct} title="Limpiar"><FiX size={13} /></button>
            )}
          </div>
        </div>
        <div className="mov-filter-actions">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={hideAnulados}
              onChange={e => { setHideAnulados(e.target.checked); setPage(1); }}
              style={{ cursor: 'pointer' }}
            />
            Ocultar anulados
          </label>
          <button className="btn-nuevo" style={{ background: '#475569' }} onClick={() => { setPage(1); loadMovements(); }}>
            <FiFilter size={13} /> Filtrar
          </button>
          <button className="btn-nuevo" onClick={() => setShowModal(true)}>
            <FiPlus size={13} /> Nuevo
          </button>
          <button
            className="btn-export-pdf"
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            title="Exportar PDF"
          >
            <FiFileText size={13} /> {exporting ? '...' : 'PDF'}
          </button>
          <button
            className="btn-export-excel"
            onClick={() => handleExport('excel')}
            disabled={exporting}
            title="Exportar Excel"
          >
            <FiDownload size={13} /> {exporting ? '...' : 'Excel'}
          </button>
        </div>
      </div>

      {/* ── BARRA RESUMEN + PAGINACIÓN ── */}
      <div className="mov-summary-bar">
        <span className="mov-summary-item"><strong>{total}</strong> movimientos</span>
        <span className="mov-summary-item green">
          <FiArrowDownCircle size={13} /> Entradas: <strong>{fmtQty(totalIn)}</strong>
        </span>
        <span className="mov-summary-item red">
          <FiArrowUpCircle size={13} /> Salidas: <strong>{fmtQty(totalOut)}</strong>
        </span>
        {productId && (
          <span className="mov-summary-item kardex-pill">
            <FiPackage size={13} />
            Kardex: <strong>{kardexProductName}</strong>
            <button className="mov-clear-btn" onClick={clearProduct}><FiX size={11} /></button>
          </span>
        )}
        <div className="mt-pagination" style={{ marginLeft: 'auto' }}>
          <button onClick={() => setPage(1)} disabled={page === 1}><FiChevronsLeft size={11} /></button>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}><FiChevronLeft size={11} /></button>
          <span className="pg-current">{page}</span>
          <span className="pg-info">/ {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}><FiChevronRight size={11} /></button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages}><FiChevronsRight size={11} /></button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* ── TABLA MOVIMIENTOS ── */}
      <div className="mt-table-wrap">
        {loading ? (
          <div className="mt-loading">Cargando movimientos...</div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr className="mt-head-row">
                <th>Fecha</th>
                <th>Almacén</th>
                <th>SKU</th>
                <th>Producto</th>
                <th>Tipo</th>
                <th>Causa</th>
                <th>Proveedor</th>
                <th style={{ textAlign: 'right' }}>Saldo Ant.</th>
                <th style={{ textAlign: 'right' }}>Movimiento</th>
                <th style={{ textAlign: 'right' }}>Saldo Act.</th>
                <th>Notas</th>
                <th>Usuario</th>
                <th></th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 ? (
                <tr className="mt-empty">
                  <td colSpan={12}>Sin movimientos para los filtros seleccionados</td>
                </tr>
              ) : movements.map((r, i) => {
                const isIn = ['ingreso','purchase'].includes(r.movement_type)
                  || r.causa === 'TRANSFERENCIA ENTRADA'
                  || r.causa === 'CONVERSION ENTRADA';
                const isSelected = String(r.product_id) === String(productId) && String(kardexWhId) === String(r.warehouse_id);
                const anulado   = !!r.anulado;
                const canAnular = ['ingreso','salida'].includes(r.movement_type) && !anulado;
                const rowCls    = anulado
                  ? 'mt-row-anulado'
                  : isSelected
                    ? 'mov-row-selected'
                    : (i % 2 === 0 ? 'mt-row-even mt-row-clickable' : 'mt-row-odd mt-row-clickable');
                return (
                  <tr
                    key={r.id}
                    className={rowCls}
                    onClick={() => !anulado && handleSelectProduct(r)}
                    title={anulado ? 'Movimiento anulado' : 'Clic para ver Kardex de este producto'}
                  >
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDt(r.moved_at)}</td>
                    <td>
                      <span className="mov-warehouse-chip">
                        <FiArchive size={11} />{r.warehouse_name || '-'}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11.5, color: '#475569' }}>
                      {r.sku || '-'}
                    </td>
                    <td style={{ fontWeight: 500 }}>{r.product_name}</td>
                    <td><TypeBadge type={r.movement_type} /></td>
                    <td style={{ fontSize: 11.5, color: '#475569' }}>{r.causa || '-'}</td>
                    <td style={{ fontSize: 11.5, color: '#475569' }}>{r.provider_name || '-'}</td>
                    <td style={{ textAlign: 'right', color: '#94a3b8' }}>{fmtQty(r.qty_before)} <em style={{ fontStyle: 'normal', fontSize: 10.5, color: '#94a3b8' }}>{r.unit_measure}</em></td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: isIn ? '#16a34a' : '#dc2626' }}>
                      <div>{isIn ? '+' : '-'}{fmtQty(r.qty_moved)} <em style={{ fontStyle: 'normal', fontSize: 10.5 }}>{r.unit_measure}</em></div>
                      {!!r.special_condition && parseInt(r.qty_unidad_moved || 0) > 0 && (
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#7c3aed' }}>
                          {isIn ? '+' : '-'}{parseInt(r.qty_unidad_moved)} UND
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtQty(r.qty_after)} <em style={{ fontStyle: 'normal', fontSize: 10.5, color: '#64748b' }}>{r.unit_measure}</em></td>
                    <td style={{ fontSize: 11, color: '#64748b', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.notes || '-'}
                    </td>
                    <td style={{ fontSize: 11.5 }}>{r.username || '-'}</td>
                    <td className="no-strike" onClick={e => e.stopPropagation()}>
                      {canAnular || anulado
                        ? <button
                            className="btn-row-del"
                            disabled={anulado}
                            onClick={() => !anulado && handleAnular(r)}
                            title={anulado ? 'Ya anulado' : 'Anular movimiento'}
                          >
                            <FiX size={13} />
                          </button>
                        : null
                      }
                    </td>
                    <td className="no-strike">
                      {anulado
                        ? <span className="badge-estado-anulado">ANULADO</span>
                        : <span className="badge-estado-activo">ACTIVO</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── KARDEX ── */}
      {productId && (
        <div className="mov-kardex-section">
          {kardexLoading ? (
            <div className="mt-loading">Cargando Kardex...</div>
          ) : (
            <KardexView rows={kardexRows} productName={kardexProductName} warehouseName={kardexWhName} />
          )}
        </div>
      )}

      {/* ── MODAL ── */}
      {showModal && (
        <ModalMovimiento
          businessId={businessId}
          warehouses={warehouses}
          onClose={() => setShowModal(false)}
          onSaved={() => { loadMovements(); loadKardex(); }}
        />
      )}
    </div>
  );
}
